import { LensSDKException, NotFoundException } from "lens-platform-sdk";
import { useLensPlatformClient } from "src/hooks/useLensPlatformClient";
import { useMutation, useQuery } from "@tanstack/react-query";
import { UserOTPPreferences } from "lens-platform-sdk";
import { useAnalytics } from "src/hooks/useAnalytics";
import { queryClient } from "src/queryClient";

/**
 * Get get user OTP preferences
 */
export const useGetUserOTPPreferences = () => {
  const lensPlatformClient = useLensPlatformClient();
  const { track } = useAnalytics();

  const {
    isLoading: loadingUserOTPPreferences,
    error: errorLoadingUserOTPPreferences,
    data: userOTPPreferences,
  } = useQuery<UserOTPPreferences | null, LensSDKException>({
    queryKey: ["user-OTP-preferences"],
    queryFn: async () => {
      try {
        const preferences = await lensPlatformClient.user.getUserOTPPreferences();

        track("OTP preferences retrieved for the user");

        return preferences;
      } catch (error) {
        if (error instanceof NotFoundException) {
          track("Failed to load OTP preferences for the user");

          return null;
        }
        throw error;
      }
    },
  });

  const {
    error: errorUpdatingUserOTPPreferences,
    mutateAsync: updateUserOTPPreferencesAsync,
    isPending: updatingUserOTPPreferences,
  } = useMutation<UserOTPPreferences, LensSDKException, UserOTPPreferences>({
    mutationFn: async (preferences) => {
      return lensPlatformClient.user.updateUserOTPPreferences(preferences);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["user-OTP-preferences"] });
    },
  });

  return {
    userOTPPreferences,
    loadingUserOTPPreferences,
    errorLoadingUserOTPPreferences,
    errorUpdatingUserOTPPreferences,
    updateUserOTPPreferencesAsync,
    updatingUserOTPPreferences,
  };
};
